'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import DrawerMenu from './DrawerMenu.react';
import {
    useMealOperations,
    useModalControls,
    useMealPlanData,
    usePermissions
} from '../contexts';

import '../../../Dashboard/PresentMenu.scss';

/**
 * PresentMenu - Functional component for Plans/Editor
 * Uses DrawerMenu for consistent UI with Dashboard
 */
const PresentMenu = ({ offset, mealType, meals, primary }) => {
    const { user, mealTypes } = useMealPlanData();
    const {
        removeMeals,
        startAddMeal
    } = useMealOperations();
    const {
        showMealDetails,
        startRepeatMeal,
        editMealBatches
    } = useModalControls();
    const { inhibitSwap } = usePermissions();

    const { inhibit_add_swap_modes = [] } = user || {};
    const isChangeServingsDisabled = !(primary && primary.meal_type === 'fresh');
    const isBatchCookDisabled = isChangeServingsDisabled;
    const isLeftover = primary && primary.meal_type === 'leftover';

    // Define available add/swap modes
    const addSwapModes = [
        'smart-choices',
        'restaurants',
        'favorites',
        'ingredients',
        'browser',
        'use-own-recipe',
        'create-custom',
    ];

    const allowedModes = addSwapModes.filter(mode => !inhibit_add_swap_modes.includes(mode));
    const defaultMode = allowedModes[0];

    return (
        <DrawerMenu
            button={<span>···</span>}
            className="ellipsis-menu-btn"
            modalClassName="feed-present-menu"
            menuTitle="More Options"
        >
            {({ closeModal }) => (
                <div className="feed-menu-content">
                    {startAddMeal && allowedModes.length && !inhibitSwap && (
                        <button
                            className="menu-item add-food"
                            onClick={() => {
                                startAddMeal(offset, mealType, { defaultMode, allowedModes });
                                closeModal();
                            }}
                        >
                            Add food <i className="icon-add-circle-outline2" />
                        </button>
                    )}

                    {!inhibitSwap && (
                        <button
                            disabled={isBatchCookDisabled}
                            className="menu-item edit-meal-servings"
                            onClick={() => {
                                editMealBatches(primary);
                                closeModal();
                            }}
                        >
                            Batch Cook <i className="icon-stacks" />
                        </button>
                    )}


                    {!isLeftover && startRepeatMeal && !inhibitSwap && (
                        <button
                            disabled={!primary}
                            className="menu-item repeat-meal"
                            onClick={() => {
                                startRepeatMeal(meals);
                                closeModal();
                            }}
                        >
                            Repeat Meal <i className="icon-calendar7" />
                        </button>
                    )}

                    {showMealDetails && (
                        <button
                            disabled={!primary}
                            className="menu-item view-details"
                            onClick={() => {
                                // Find the full mealType object from the mealTypes array
                                const fullMealType = mealTypes.find(mt => mt.name === mealType) || { name: mealType };
                                showMealDetails({ meals, offset, mealType: fullMealType });
                                closeModal();
                            }}
                        >
                            View meal details <i className="icon-pencil-notebook" />
                        </button>
                    )}

                    {!inhibitSwap && (
                        <button
                            disabled={isChangeServingsDisabled}
                            className="menu-item change-participants"
                            onClick={() => {
                                editMealBatches(primary);
                                closeModal();
                            }}
                        >
                            Change servings <i className="icon-silverware4" />
                        </button>
                    )}

                    {removeMeals && !inhibitSwap && (
                        <button
                            disabled={!primary}
                            className="menu-item skip-meal"
                            onClick={() => {
                                removeMeals(meals.map(meal => meal.uuid || meal.id));
                                closeModal();
                            }}
                        >
                            Remove this meal <i className="icon-trash-can4" />
                        </button>
                    )}
                </div>
            )}
        </DrawerMenu>
    );
};

PresentMenu.propTypes = {
    offset: PropTypes.number,
    mealType: PropTypes.string,
    meals: PropTypes.array.isRequired,
    primary: PropTypes.object,
};

export default PresentMenu;
